<template>

	<div class="grid grid-cols-6 xl:grid-cols-12 gap-4 max-w-6xl mx-auto py-6 px-1">

    <div class="col-span-full xl:col-span-8">
      <div class="col-span-full mt-2 mb-5">
        <h4 data-cy="asset-data-rxswin-summary-label" class="text-xl font-bold text-surface-600 antialiased">{{ $t('pages.assets.asset.summary.asset_data') }}</h4>
      </div>

      <Card>
        <template #content>
          <DescriptionList data-cy="asset-summary-rxswin-list" :title="$t('common.information')" class="-mt-6">
            <DescriptionListItem :term="$t('common.company')" :details="asset.company?.name"/>
            <DescriptionListItem :term="$t('common.name')" :details="asset.name"/>
            <DescriptionListItem :term="$t('common.manufacturer')" :details="asset.manufacturer?.name"/>
            <DescriptionListItem :term="$t('common.asset_type')" :details="asset.asset_type?.name"/>
            <DescriptionListItem :term="$t('common.stage')" :details="stageLabel"/>
            <DescriptionListItem :term="$t('common.vin')" :details="asset.vin"/>
            <DescriptionListItem :term="$t('common.chassis_number')" :details="asset.chassis_number"/>
          </DescriptionList>
        </template>
      </Card>
    </div>

    <div class="col-span-full mt-8">
      <div class="col-span-full mt-2 mb-5">
        <h4 class="text-xl font-bold text-surface-600 antialiased">{{ $t('pages.assets.asset.summary.rx_swins') }}</h4>
      </div>
      <div v-if="asset?.rx_swins?.length > 0" class="flex flex-wrap gap-2">
        <Tag
            v-for="rxSwin in asset?.rx_swins"
            class="cursor-pointer"
            :severity="rxSwin.selected ? 'info' : 'secondary'"
            :value="`${rxSwin.regulation.name}_${rxSwin.identifier}`"
            @click="highlightTag(rxSwin)"
        />
      </div>
      <p v-else class="text-surface-400">{{ $t('pages.assets.asset.summary.no_rx_swins') }}</p>
    </div>

    <div class="col-span-full mt-8">
      <h4 class="text-xl font-bold text-surface-600 antialiased">{{ $t('pages.assets.asset.summary.active_devices') }}</h4>
    </div>

    <template v-if="activeDevices.length > 0">
      <DeviceCard :device="device" v-for="device in activeDevices" :key="device.id" class="col-span-2 xl:col-span-3"/>
    </template>
    <div v-else class="col-span-full">
      <p class="text-surface-400">{{ $t('pages.assets.asset.summary.no_devices') }}</p>
    </div>

    <template v-if="oldDevices.length > 0">
      <div class="col-span-full mt-8">
        <h4 class="text-xl font-bold text-surface-600 antialiased">{{ $t('pages.assets.asset.summary.old_devices') }}</h4>
      </div>

      <DeviceCard :device="device" v-for="device in oldDevices" :key="device.id" :hide-status="true" class="col-span-2 xl:col-span-3"/>
    </template>
	</div>
</template>

<script>
import { mapStores } from "pinia";
import { useAssetsStore } from "@/stores/assets.js";
import { IconCheck } from '@tabler/icons-vue';
import DescriptionList from "@/Components/DescriptionList.vue";
import DescriptionListItem from "@/Components/DescriptionListItem.vue";
import DeviceCard from "@/Widgets/Cards/DeviceCard.vue";

export default {
	name: "AssetSummaryView",
	components: {
		DeviceCard,
		DescriptionListItem,
		DescriptionList,
		IconCheck,
	},
	emits: [
		'on-save',
		'on-delete',
	],
	computed: {
		...mapStores(useAssetsStore),
		asset() {
			return this.$attrs.asset;
    },
    stageLabel() {
      if (!this.asset?.stage) return null;
      if (this.asset.stage === 'complete') return 'Complete';
      if (this.asset.stage === 'first_stage') return 'First stage';
      return this.asset.stage;
    },
    activeDevices() {
      return this.asset?.devices?.filter(d => !!d.pivot?.active) ?? [];
    },
    oldDevices() {
      return this.asset?.devices?.filter(d => !d.pivot?.active) ?? [];
    }
	},
	methods: {
		highlightTag(rxSwin) {
      let matchedAsset = this.asset?.rx_swins.find(r => r.id === rxSwin.id);
      if (matchedAsset.selected === true) { // we've selected the same chip again
        matchedAsset.selected = !matchedAsset.selected; // flip value to false
        this.resetHighlightedDevices(); // clear all highlights
      } else {
        this.resetHighlightedDevices(); // clear first - remove old highlights
        matchedAsset.selected = true; // then highlight the new one
      }
      if (!matchedAsset.selected) return; // break here if no chips are selected

      rxSwin.mappings.forEach(mapping => {
        this.asset.devices?.filter(d => {
          if ((d.device_revision_id === mapping.device_revision_id) &&
              ((d.current_firmware_id === mapping.firmware_id))) {
            d.highlighted = true;
          }
        });
      });
    },
		resetHighlightedDevices() {
      this.asset?.rx_swins.forEach(rxSwin => {
        rxSwin.selected = false;
      });
      this.asset.devices.forEach(device => {
        device.highlighted = false;
      });
    }
	},
}
</script>


<style scoped>
</style>